/**
 * Verify Document Dialog Component
 * Combines QR scanner with document signature verification.
 * Used in the header for public document verification.
 *
 * Flow:
 * 1. Opens camera scanner
 * 2. On scan: verifies Ed25519 signature locally (offline capable)
 * 3. Displays document data or error
 * 4. Offers PDF download if online
 */

import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  QrCodeScanner as QrCodeIcon,
  Close as CloseIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';

import {
  isSignedQRCode,
  verifySignature,
  type VerificationResult,
} from '../../utils/documentVerification';
import DocumentVerificationResult from './DocumentVerificationResult';

interface VerifyDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional hook fired right after a QR code has been decoded and verified.
   * Used by authenticated surfaces (sidebar, dashboard header) to log the scan
   * into the global event history. Not wired on the public home page.
   */
  onScanComplete?: (decodedText: string, result: VerificationResult) => void;
}

const SCANNER_CONTAINER_ID = 'qr-verify-scanner-region';

export default function VerifyDocumentDialog({
  open,
  onClose,
  onScanComplete,
}: VerifyDocumentDialogProps) {
  const { t } = useTranslation();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) {
          await scannerRef.current.stop();
        }
      } catch {
        // Ignore
      }
      try {
        scannerRef.current.clear();
      } catch {
        // Ignore
      }
      scannerRef.current = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;

    setError(null);
    setIsStarting(true);
    setVerificationResult(null);

    const container = document.getElementById(SCANNER_CONTAINER_ID);
    if (!container) {
      setIsStarting(false);
      return;
    }

    const scanner = new Html5Qrcode(SCANNER_CONTAINER_ID);
    scannerRef.current = scanner;

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
    };

    const onSuccess = (decodedText: string) => {
      // Stop scanner first
      scanner
        .stop()
        .then(() => scanner.clear())
        .catch(() => {});
      scannerRef.current = null;

      // Verify the scanned QR code
      let result: VerificationResult;
      if (isSignedQRCode(decodedText)) {
        result = verifySignature(decodedText);
      } else if (
        decodedText.includes('/api/v1/documents/verify/') ||
        decodedText.includes('/api/v1/invoices/') ||
        decodedText.includes('bnft')
      ) {
        // Legacy BNFT URL format - cannot verify offline
        result = { valid: false, error: 'LEGACY_FORMAT' };
      } else {
        // Completely unrecognized QR code
        result = { valid: false, error: 'UNRECOGNIZED_QR' };
      }

      setVerificationResult(result);

      // Fire-and-forget audit hook for authenticated callers.
      if (onScanComplete) {
        try {
          onScanComplete(decodedText, result);
        } catch {
          // Never let logging break the scan flow.
        }
      }
    };

    // Try back camera first, then front camera
    try {
      await scanner.start(
        { facingMode: 'environment' },
        config,
        onSuccess,
        () => {},
      );
      setIsStarting(false);
      return;
    } catch {
      // Try front camera
    }

    try {
      await scanner.start(
        { facingMode: 'user' },
        config,
        onSuccess,
        () => {},
      );
      setIsStarting(false);
    } catch (err: any) {
      setIsStarting(false);
      scannerRef.current = null;
      if (err?.toString?.().includes('NotAllowedError')) {
        setError(t('common.qrScanner.cameraPermissionDenied'));
      } else if (err?.toString?.().includes('NotFoundError')) {
        setError(t('common.qrScanner.noCameraFound'));
      } else {
        setError(t('common.qrScanner.cameraError'));
      }
    }
  }, [t, onScanComplete]);

  const handleClose = useCallback(() => {
    stopScanner();
    setError(null);
    setIsStarting(false);
    setVerificationResult(null);
    onClose();
  }, [stopScanner, onClose]);

  const handleRetry = useCallback(() => {
    setVerificationResult(null);
    setError(null);
    // Use a short delay to let React re-render the scanner container first
    setTimeout(() => {
      startScanner();
    }, 100);
  }, [startScanner]);

  const handleContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && open && !scannerRef.current && !verificationResult) {
        requestAnimationFrame(() => {
          startScanner();
        });
      }
    },
    [open, startScanner, verificationResult],
  );

  // Clean up scanner when dialog closes
  if (!open && scannerRef.current) {
    stopScanner();
  }

  const showScanner = !verificationResult;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCodeIcon />
        {t('common.documentVerification.title')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scanner view */}
        {showScanner && (
          <>
            <Box
              ref={handleContainerRef}
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: 350,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isStarting && (
                <Box
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    {t('common.qrScanner.starting')}
                  </Typography>
                </Box>
              )}
              <Box
                id={SCANNER_CONTAINER_ID}
                sx={{
                  width: '100%',
                  minHeight: 300,
                  '& video': {
                    borderRadius: 1,
                    width: '100% !important',
                  },
                }}
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 2 }}
            >
              {t('common.documentVerification.scanInstructions')}
            </Typography>
          </>
        )}

        {/* Verification result */}
        {verificationResult && (
          <DocumentVerificationResult result={verificationResult} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {verificationResult && (
          <Button onClick={handleRetry} startIcon={<RetryIcon />}>
            {t('common.documentVerification.scanAgain')}
          </Button>
        )}
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
